import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, UserPlus, Swords, Send, MessageSquareText, Copy, Camera, CheckCircle2, XCircle, Stethoscope, ClipboardCheck, FileText, Trash2, MailWarning, Link2, ArrowUp, ArrowDown, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inputCls = "w-full h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white";
const EMPTY_PRE = { heart_rate: "", bp_systolic: "", bp_diastolic: "", respiratory_rate: "", spo2: "", temperature: "", consciousness: "", blood_glucose: "", pupils: "", fit_to_fight: false, notes: "" };
const EMPTY_POST = { heart_rate: "", bp_systolic: "", bp_diastolic: "", respiratory_rate: "", spo2: "", temperature: "", consciousness: "", blood_glucose: "", pupils: "", treatment: "", notes: "", create_patient: true };
const OBS_FIELDS = [["heart_rate", "HR (bpm)"], ["bp_systolic", "BP systolic"], ["bp_diastolic", "BP diastolic"], ["respiratory_rate", "Resp rate"], ["spo2", "SpO2 %"], ["temperature", "Temp °C"], ["blood_glucose", "BM (mmol/L)"], ["pupils", "Pupils (e.g. PEARL)"]];

function ObsFields({ values, onChange, prefix }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
      {OBS_FIELDS.map(([k, label]) => (
        <label key={k} className="block">
          <span className="text-[11px] font-semibold text-slate-500 mb-0.5 block">{label}</span>
          <input value={values[k]} onChange={(e) => onChange(k, e.target.value)}
            data-testid={`${prefix}-${k}`}
            className="w-full h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white" />
        </label>
      ))}
      <label className="block sm:col-span-2">
        <span className="text-[11px] font-semibold text-slate-500 mb-0.5 block">Consciousness (AVPU)</span>
        <select value={values.consciousness} onChange={(e) => onChange("consciousness", e.target.value)}
          data-testid={`${prefix}-consciousness`}
          className="w-full h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white">
          <option value="">—</option>
          {["Alert", "Voice", "Pain", "Unresponsive"].map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </label>
    </div>
  );
}

function buildObs(src) {
  const obs = {};
  ["heart_rate", "bp_systolic", "bp_diastolic", "respiratory_rate", "spo2", "temperature", "blood_glucose"].forEach((k) => {
    if (src[k] !== "" && src[k] !== undefined) obs[k] = Number(src[k]);
  });
  if (src.consciousness) obs.consciousness = src.consciousness;
  if (src.pupils) obs.pupils = src.pupils;
  return obs;
}

function Headshot({ fighter, size = "h-14 w-14" }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    if (fighter.has_headshot) {
      api.get(`/fighters/${fighter.id}/headshot`, { responseType: "blob" })
        .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
        .catch(() => {});
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [fighter.id, fighter.has_headshot]);
  if (!src) {
    return <div className={`${size} rounded-full bg-slate-200 grid place-items-center shrink-0`} data-testid={`headshot-empty-${fighter.id}`}><Camera className="h-5 w-5 text-slate-400" /></div>;
  }
  return <img src={src} alt={fighter.name} className={`${size} rounded-full object-cover shrink-0 border-2 border-white shadow`} data-testid={`headshot-${fighter.id}`} />;
}

function Chip({ ok, label, testid }) {
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2 py-0.5 ${ok ? "bg-emerald-50 text-[#007F3B]" : "bg-amber-50 text-[#ED8B00]"}`}>
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />} {label}
    </span>
  );
}

function FighterPanel({ f, corner, onOpen }) {
  const red = corner === "red";
  const blue = corner === "blue";
  return (
    <button onClick={() => onOpen(f)} data-testid={`fighter-card-${f.id}`}
      className={`flex-1 rounded-xl border-2 p-3 text-left transition-colors bg-white min-w-0 ${red ? "border-red-300 hover:border-red-500" : blue ? "border-sky-300 hover:border-[#005EB8]" : "border-slate-200 hover:border-slate-400"}`}>
      <div className="flex items-center gap-2.5">
        <Headshot fighter={f} />
        <div className="min-w-0">
          <p className="font-bold text-sm text-[#0F2547] truncate">{f.name}</p>
          <p className={`text-[10px] font-black uppercase tracking-wider ${red ? "text-red-600" : blue ? "text-[#005EB8]" : "text-slate-400"}`}>
            {f.corner ? `${f.corner} corner` : "corner not chosen"}
          </p>
        </div>
      </div>
      <div className="flex gap-1 mt-2 flex-wrap">
        <Chip ok={!!f.form} label="Form" testid={`chip-form-${f.id}`} />
        <Chip ok={f.has_headshot} label="Photo" testid={`chip-photo-${f.id}`} />
        <Chip ok={!!f.precheck} label={f.precheck?.fit_to_fight ? "Fit to fight" : "Pre-fight"} testid={`chip-pre-${f.id}`} />
        <Chip ok={!!f.postcheck} label="Post-fight" testid={`chip-post-${f.id}`} />
      </div>
    </button>
  );
}

export default function FightCard() {
  const { eid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [newFighter, setNewFighter] = useState({ name: "", email: "", phone: "", corner: "" });
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(null);
  const [preOpen, setPreOpen] = useState(null);
  const [postOpen, setPostOpen] = useState(null);
  const [chaseOpen, setChaseOpen] = useState(false);
  const [chaseEmail, setChaseEmail] = useState("");
  const [preForm, setPreForm] = useState(EMPTY_PRE);
  const [postForm, setPostForm] = useState(EMPTY_POST);
  const [editOpen, setEditOpen] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "", corner: "" });
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/${eid}/fight-card`);
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  const fighters = data?.fighters || [];
  const missing = fighters.filter((f) => !f.form);
  const q = search.trim().toLowerCase();
  const fmatch = (f) => !q || (f?.name || "").toLowerCase().includes(q);
  const doneSet = new Set(data?.fights_done || []);
  const redsAll = fighters.filter((f) => f.corner === "red");
  const bluesAll = fighters.filter((f) => f.corner === "blue");
  const reds = redsAll.filter(fmatch);
  const blues = bluesAll.filter(fmatch);
  const noCorner = fighters.filter((f) => f.corner !== "red" && f.corner !== "blue").filter(fmatch);
  const rowCount = Math.max(reds.length, blues.length);
  const pairKey = (a, b) => [a, b].sort().join("|");
  const eventLink = data ? `${window.location.origin}/fight-form/event/${data.event.fight_form_token}` : "";
  const fighterLink = (f) => `${window.location.origin}/fight-form/${f.form_token}`;

  const act = async (fn) => {
    try {
      const r = await fn();
      if (r?.data?.message) toast.success(r.data.message);
      load();
      return r;
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addFighter = async (e, allowDuplicate = false) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/events/${eid}/fighters`, { ...newFighter, allow_duplicate: allowDuplicate });
      toast.success("Fighter added");
      setNewFighter({ name: "", email: "", phone: "", corner: "" });
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (err?.response?.status === 409 && !allowDuplicate) {
        if (window.confirm(`${detail}.\n\nIf this is a different person with the same name, press OK to add them anyway — they'll get their own form link to fill in all their own details.`)) {
          setSaving(false);
          return addFighter(e, true);
        }
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  const moveFighter = async (f, dir) => {
    const col = f.corner === "red" ? redsAll : bluesAll;
    const order = col.map((x) => x.id);
    const i = order.indexOf(f.id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    await act(() => api.post(`/events/${eid}/fighters/reorder`, { corner: f.corner, order }));
  };

  const toggleDone = async (a, b, done) => {
    await act(() => api.post(`/events/${eid}/fight-done`, { fighter_a: a.id, fighter_b: b.id, done }));
  };

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text).then(() => toast.success(`${label} copied`)).catch(() => toast.error("Copy failed"));
  };

  const savePrecheck = async () => {
    setSaving(true);
    const r = await act(() => api.post(`/fighters/${preOpen.id}/precheck`, { ...buildObs(preForm), fit_to_fight: preForm.fit_to_fight, notes: preForm.notes }));
    setSaving(false);
    if (r) { setPreOpen(null); setOpen(null); }
  };

  const savePostcheck = async () => {
    setSaving(true);
    const r = await act(() => api.post(`/fighters/${postOpen.id}/postcheck`, { ...buildObs(postForm), treatment: postForm.treatment, notes: postForm.notes, create_patient: postForm.create_patient }));
    setSaving(false);
    if (r) { setPostOpen(null); setOpen(null); }
  };

  const saveEdit = async () => {
    setSaving(true);
    const r = await act(() => api.patch(`/fighters/${editOpen.id}`, editForm));
    setSaving(false);
    if (r) { setEditOpen(null); }
  };

  const sel = open ? fighters.find((f) => f.id === open.id) || open : null;

  useEffect(() => {
    if (sel?.id) {
      api.get(`/fighters/${sel.id}/history`).then((r) => setHistory(r.data.history || [])).catch(() => setHistory([]));
    } else {
      setHistory([]);
    }
  }, [sel?.id]);

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="fight-card-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
          <button onClick={() => navigate("/calendar")} data-testid="fight-card-back"
            className="h-11 w-11 grid place-items-center rounded-lg hover:bg-slate-100 transition-colors">
            <ChevronLeft className="h-5 w-5 text-[#0F2547]" />
          </button>
          <div className="min-w-0">
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-[#0F2547] flex items-center gap-2" data-testid="fight-card-title">
              <Swords className="h-5 w-5 text-[#005EB8]" /> Fight card
            </h1>
            {data && <p className="text-xs text-slate-500 truncate">{data.event.name}{data.event.event_date ? ` · ${data.event.event_date}` : ""}{data.event.venue ? ` · ${data.event.venue}` : ""}</p>}
          </div>
          {isAdmin && (
            <div className="ml-auto flex gap-2 flex-wrap">
              <button onClick={() => copy(eventLink, "Boxer form link")} data-testid="copy-event-link-btn"
                className="h-10 px-3 rounded-lg border border-slate-300 text-slate-600 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors text-xs font-bold inline-flex items-center gap-1.5 bg-white"
                title="Generic link — every boxer fills in their own details; nobody can see or edit anyone else's form">
                <Link2 className="h-3.5 w-3.5" /> Copy boxer link (generic)
              </button>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/events/${eid}/fight-card/report`} target="_blank" rel="noreferrer" data-testid="fight-report-btn"
                className="h-10 px-3 rounded-lg border border-slate-300 text-slate-600 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors text-xs font-bold inline-flex items-center gap-1.5 bg-white">
                <FileText className="h-3.5 w-3.5" /> Report
              </a>
              <button onClick={() => setChaseOpen(true)} data-testid="chase-missing-btn"
                className="h-10 px-3 rounded-lg bg-[#ED8B00] hover:bg-[#c97600] transition-colors text-white text-xs font-bold inline-flex items-center gap-1.5">
                <MailWarning className="h-3.5 w-3.5" /> Email missing forms ({missing.length})
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5">
        {fighters.length > 0 && (
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search fighter name…"
            data-testid="fighter-search"
            className="w-full h-12 rounded-xl border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
        )}
        {isAdmin && (
          <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="add-fighter-panel">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Add fighter</p>
            <form onSubmit={addFighter} className="grid sm:grid-cols-[1fr_1fr_1fr_120px_auto] gap-2">
              <input required placeholder="Fighter name *" value={newFighter.name}
                onChange={(e) => setNewFighter((f) => ({ ...f, name: e.target.value }))}
                data-testid="fighter-name-input" className={inputCls} />
              <input type="email" placeholder="Email (for the form link)" value={newFighter.email}
                onChange={(e) => setNewFighter((f) => ({ ...f, email: e.target.value }))}
                data-testid="fighter-email-input" className={inputCls} />
              <input type="tel" placeholder="Mobile (for a text)" value={newFighter.phone}
                onChange={(e) => setNewFighter((f) => ({ ...f, phone: e.target.value }))}
                data-testid="fighter-phone-input" className={inputCls} />
              <select value={newFighter.corner} onChange={(e) => setNewFighter((f) => ({ ...f, corner: e.target.value }))}
                data-testid="fighter-corner-select" className={inputCls}>
                <option value="">Corner…</option>
                <option value="red">Red</option>
                <option value="blue">Blue</option>
              </select>
              <button type="submit" disabled={saving} data-testid="add-fighter-btn"
                className="h-11 px-4 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
                <UserPlus className="h-4 w-4" /> Add
              </button>
            </form>
            <p className="text-[11px] text-slate-400 mt-2">Assign each fighter a corner — the card pairs the rows automatically: red 1 vs blue 1, red 2 vs blue 2, and so on. Use the arrows to move fighters up or down their corner.</p>
          </div>
        )}

        {data === null ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : fighters.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="fight-card-empty">
            <Swords className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No fighters on the card yet{isAdmin ? " — add them above, then share the boxer link so they can complete their medical form." : "."}</p>
          </div>
        ) : (
          <>
            {Array.from({ length: rowCount }).map((_, i) => {
              const a = reds[i];
              const b = blues[i];
              const done = !!(a && b && doneSet.has(pairKey(a.id, b.id)));
              const arrows = (f, col) => (
                <div className="flex flex-col justify-center gap-1 shrink-0">
                  <button onClick={() => moveFighter(f, -1)} disabled={col.findIndex((x) => x.id === f.id) <= 0}
                    data-testid={`fighter-up-${f.id}`} aria-label={`Move ${f.name} up`}
                    className="h-7 w-7 grid place-items-center rounded-md text-slate-300 hover:text-white hover:bg-white/10 disabled:opacity-20 transition-colors">
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => moveFighter(f, 1)} disabled={col.findIndex((x) => x.id === f.id) >= col.length - 1}
                    data-testid={`fighter-down-${f.id}`} aria-label={`Move ${f.name} down`}
                    className="h-7 w-7 grid place-items-center rounded-md text-slate-300 hover:text-white hover:bg-white/10 disabled:opacity-20 transition-colors">
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
              return (
                <div key={i} className={`bg-[#0F2547] rounded-2xl p-4 transition-opacity ${done ? "opacity-70" : ""}`} data-testid={`fight-row-${i + 1}`}>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-white font-black uppercase tracking-[0.2em] text-xs">Fight {i + 1} of {rowCount}</p>
                    {done && (
                      <span className="inline-flex items-center gap-1 text-[#7ee2a8] text-[11px] font-black uppercase tracking-wider" data-testid={`fight-done-label-${i + 1}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Done
                      </span>
                    )}
                  </div>
                  <div className="flex items-stretch gap-2">
                    <div className="flex-1 min-w-0 flex items-stretch gap-1.5">
                      {isAdmin && !q && a && arrows(a, redsAll)}
                      {a ? <FighterPanel f={a} corner="red" onOpen={setOpen} /> : (
                        <div className="flex-1 rounded-xl border-2 border-dashed border-white/20 grid place-items-center p-3 text-white/40 text-[11px] font-bold" data-testid={`fight-row-${i + 1}-no-red`}>
                          No red fighter in this row
                        </div>
                      )}
                    </div>
                    <div className="grid place-items-center shrink-0 px-1">
                      <span className="text-white font-black text-sm tracking-widest">VS</span>
                    </div>
                    <div className="flex-1 min-w-0 flex items-stretch gap-1.5">
                      {b ? <FighterPanel f={b} corner="blue" onOpen={setOpen} /> : (
                        <div className="flex-1 rounded-xl border-2 border-dashed border-white/20 grid place-items-center p-3 text-white/40 text-[11px] font-bold" data-testid={`fight-row-${i + 1}-no-blue`}>
                          No blue fighter in this row
                        </div>
                      )}
                      {isAdmin && !q && b && arrows(b, bluesAll)}
                    </div>
                  </div>
                  {a && b && (
                    <button onClick={() => toggleDone(a, b, !done)} data-testid={`fight-done-toggle-${i + 1}`}
                      className={`mt-3 w-full h-11 rounded-lg text-xs font-black uppercase tracking-widest inline-flex items-center justify-center gap-2 transition-colors ${done ? "bg-[#007F3B] text-white hover:bg-[#006631]" : "bg-white/10 text-white hover:bg-white/20 border border-white/25"}`}>
                      <CheckCircle2 className="h-4 w-4" /> {done ? "Fight done — tap to reopen" : "Tick when this fight is done"}
                    </button>
                  )}
                </div>
              );
            })}
            {noCorner.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="unpaired-section">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">No corner chosen yet</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {noCorner.map((f) => <FighterPanel key={f.id} f={f} corner={f.corner} onOpen={setOpen} />)}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      <Dialog open={!!sel} onOpenChange={() => setOpen(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="fighter-dialog">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-[#0F2547] flex items-center gap-3">
                  <Headshot fighter={sel} size="h-12 w-12" /> {sel.name}
                  {sel.corner && <span className={`text-[10px] font-black uppercase tracking-wider rounded-full px-2 py-0.5 ${sel.corner === "red" ? "bg-red-50 text-red-600" : "bg-sky-50 text-[#005EB8]"}`}>{sel.corner} corner</span>}
                </DialogTitle>
              </DialogHeader>
              <div className="flex gap-1.5 flex-wrap mb-3">
                <Chip ok={!!sel.form} label="Medical form" testid="dlg-chip-form" />
                <Chip ok={sel.has_headshot} label="Headshot" testid="dlg-chip-photo" />
                <Chip ok={!!sel.precheck} label={sel.precheck?.fit_to_fight ? "Fit to fight" : "Pre-fight check"} testid="dlg-chip-pre" />
                <Chip ok={!!sel.postcheck} label="Post-fight check" testid="dlg-chip-post" />
              </div>

              {isAdmin && (
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Corner:</span>
                  {["red", "blue"].map((c) => (
                    <button key={c} onClick={async () => { await act(() => api.patch(`/fighters/${sel.id}`, { corner: c })); }}
                      data-testid={`corner-set-${c}`}
                      className={`h-9 px-4 rounded-lg border-2 text-xs font-black uppercase tracking-widest transition-colors ${sel.corner === c ? (c === "red" ? "border-red-500 bg-red-50 text-red-600" : "border-[#005EB8] bg-sky-50 text-[#005EB8]") : "border-slate-200 text-slate-400"}`}>
                      {c}
                    </button>
                  ))}
                </div>
              )}

              <div className="flex gap-2 flex-wrap mb-3">
                <button onClick={() => copy(fighterLink(sel), "Form link")} data-testid="fighter-copy-link"
                  className="h-10 px-3 rounded-lg border border-slate-300 text-slate-600 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors text-xs font-bold inline-flex items-center gap-1.5 bg-white">
                  <Copy className="h-3.5 w-3.5" /> Copy link
                </button>
                {isAdmin && (
                  <>
                    <button onClick={() => act(() => api.post(`/fighters/${sel.id}/send-link`, { via: "email" }))} data-testid="fighter-email-link"
                      className="h-10 px-3 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-xs font-bold inline-flex items-center gap-1.5">
                      <Send className="h-3.5 w-3.5" /> Email link
                    </button>
                    <button onClick={() => act(() => api.post(`/fighters/${sel.id}/send-link`, { via: "sms" }))} data-testid="fighter-sms-link"
                      className="h-10 px-3 rounded-lg bg-[#007F3B] hover:bg-[#006631] transition-colors text-white text-xs font-bold inline-flex items-center gap-1.5">
                      <MessageSquareText className="h-3.5 w-3.5" /> Text link
                    </button>
                  </>
                )}
              </div>

              {sel.form ? (
                <div className="rounded-xl border border-slate-200 bg-[#F4F7FA] p-3 text-xs space-y-1.5 mb-3" data-testid="fighter-form-data">
                  <p><span className="font-bold">Name:</span> {sel.form.full_name} · <span className="font-bold">DOB:</span> {sel.form.dob}</p>
                  <p><span className="font-bold">Address:</span> {sel.form.address}, {sel.form.postcode}</p>
                  <p><span className="font-bold">Mobile:</span> {sel.form.mobile} · <span className="font-bold">Email:</span> {sel.form.email}</p>
                  <p><span className="font-bold">Next of kin:</span> {sel.form.nok_name} ({sel.form.nok_phone})</p>
                  <p><span className="font-bold">Screening:</span> {sel.form.screening.length ? sel.form.screening.join("; ") : "None ticked"}{sel.form.screening_other ? ` — ${sel.form.screening_other}` : ""}</p>
                  <p><span className="font-bold">Allergies:</span> {sel.form.allergies} · <span className="font-bold">Medications:</span> {sel.form.medications}</p>
                  <p><span className="font-bold">PMH:</span> {sel.form.pmh.length ? sel.form.pmh.join("; ") : "None ticked"}{sel.form.pmh_other ? ` — ${sel.form.pmh_other}` : ""}</p>
                  {sel.form.nhs_number ? <p><span className="font-bold">NHS number:</span> {sel.form.nhs_number}</p> : null}
                  {sel.form.insurance ? <p><span className="font-bold">Insurance:</span> {sel.form.insurance}</p> : null}
                  <p><span className="font-bold">UK resident:</span> {sel.form.lives_in_uk ? "Yes" : "No"} · <span className="font-bold">Declaration signed</span> {sel.form_submitted_at ? `· submitted ${new Date(sel.form_submitted_at).toLocaleString("en-GB")}` : ""}</p>
                </div>
              ) : (
                <p className="text-xs text-[#ED8B00] font-semibold bg-amber-50 rounded-lg px-3 py-2 mb-3" data-testid="fighter-no-form">Medical form not submitted yet — send the link above.</p>
              )}

              {(sel.prechecks || []).map((p, i) => (
                <div key={`${p.by}-${p.at}`} className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-xs mb-2" data-testid={`precheck-entry-${i}`}>
                  <p className="font-bold text-[#007F3B] mb-1">Pre-fight check {sel.prechecks.length > 1 ? `#${i + 1} ` : ""}— {p.fit_to_fight ? "FIT TO FIGHT" : "not cleared"}</p>
                  <p className="text-slate-600">
                    {Object.entries(p.obs || {}).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(" · ") || "No obs recorded"}
                  </p>
                  {p.notes && <p className="text-slate-600 mt-1">{p.notes}</p>}
                  <p className="text-slate-400 mt-1">{p.by} · {new Date(p.at).toLocaleString("en-GB")}</p>
                </div>
              ))}

              {(sel.postchecks || []).map((p, i) => (
                <div key={`${p.by}-${p.at}`} className="rounded-xl border border-slate-200 bg-[#F4F7FA] p-3 text-xs mb-2" data-testid={`postcheck-entry-${i}`}>
                  <p className="font-bold text-[#0F2547] mb-1">Post-fight check {sel.postchecks.length > 1 ? `#${i + 1}` : ""}</p>
                  {Object.keys(p.obs || {}).length > 0 && (
                    <p className="text-slate-600">{Object.entries(p.obs).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(" · ")}</p>
                  )}
                  {p.treatment && <p className="text-slate-600 mt-1"><span className="font-bold">Treatment:</span> {p.treatment}</p>}
                  {p.notes && <p className="text-slate-600 mt-1">{p.notes}</p>}
                  <p className="text-slate-400 mt-1">{p.by} · {new Date(p.at).toLocaleString("en-GB")}</p>
                </div>
              ))}

              {history.length > 0 && (
                <div className="rounded-xl border border-slate-200 p-3 text-xs mb-3" data-testid="fighter-history">
                  <p className="font-bold text-slate-500 uppercase tracking-wider text-[10px] mb-1.5">Other fight cards for this fighter</p>
                  {history.map((h) => (
                    <p key={h.id} className="py-0.5 text-slate-600">
                      <span className="font-semibold text-[#0F2547]">{h.event_name}</span>{h.event_date ? ` · ${h.event_date}` : ""}
                      {h.corner ? ` · ${h.corner} corner` : ""} — {h.submitted ? "form in" : "no form"}{h.precheck_done ? ", pre-check done" : ""}{h.postcheck_done ? ", post-check done" : ""}
                    </p>
                  ))}
                </div>
              )}

              <div className="flex gap-2 flex-wrap">
                {sel.form && (
                  <button onClick={() => { setPreForm(EMPTY_PRE); setPreOpen(sel); }} data-testid="open-precheck-btn"
                    className="h-11 px-4 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-xs font-bold inline-flex items-center gap-1.5">
                    <Stethoscope className="h-4 w-4" /> {sel.precheck ? "Add pre-fight obs" : "Pre-fight check"}
                  </button>
                )}
                {sel.precheck && (
                  <button onClick={() => { setPostForm(EMPTY_POST); setPostOpen(sel); }} data-testid="open-postcheck-btn"
                    className="h-11 px-4 rounded-lg bg-[#007F3B] hover:bg-[#006631] transition-colors text-white text-xs font-bold inline-flex items-center gap-1.5">
                    <ClipboardCheck className="h-4 w-4" /> {sel.postcheck ? "Add post-fight obs" : "Post-fight check"}
                  </button>
                )}
                {sel.patient_id && (
                  <button onClick={() => navigate(`/patients/${sel.patient_id}`)} data-testid="open-prf-btn"
                    className="h-11 px-4 rounded-lg border-2 border-[#005EB8] text-[#005EB8] hover:bg-sky-50 transition-colors text-xs font-bold inline-flex items-center gap-1.5 bg-white">
                    <FileText className="h-4 w-4" /> Open patient PRF
                  </button>
                )}
                {isAdmin && (
                  <button onClick={() => { setEditForm({ name: sel.name, email: sel.email || "", phone: sel.phone || "", corner: sel.corner || "" }); setEditOpen(sel); }} data-testid="fighter-edit-btn"
                    className="h-11 px-3 rounded-lg border border-slate-300 text-slate-600 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors text-xs font-bold inline-flex items-center gap-1.5 bg-white">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                )}
                {isAdmin && (
                  <button onClick={async () => { if (window.confirm(`Remove ${sel.name} from the fight card?`)) { await act(() => api.delete(`/fighters/${sel.id}`)); setOpen(null); } }} data-testid="fighter-delete-btn"
                    className="h-11 px-3 rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 transition-colors text-xs font-bold inline-flex items-center gap-1.5 ml-auto">
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!preOpen} onOpenChange={() => setPreOpen(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="precheck-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">Pre-fight check — {preOpen?.name}</DialogTitle>
          </DialogHeader>
          <ObsFields values={preForm} onChange={(k, v) => setPreForm((f) => ({ ...f, [k]: v }))} prefix="pre" />
          <textarea rows={2} placeholder="Check notes (anything found, concerns…)" value={preForm.notes}
            onChange={(e) => setPreForm((f) => ({ ...f, notes: e.target.value }))}
            data-testid="pre-notes" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mt-2 focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
          <label className="flex items-center gap-2.5 rounded-xl border-2 border-emerald-300 bg-emerald-50/60 px-3 py-3 mt-2 cursor-pointer">
            <input type="checkbox" checked={preForm.fit_to_fight} onChange={(e) => setPreForm((f) => ({ ...f, fit_to_fight: e.target.checked }))}
              data-testid="pre-fit-checkbox" className="h-5 w-5 accent-[#007F3B]" />
            <span className="text-sm font-bold text-[#007F3B]">Fit to fight — pre-fight check passed</span>
          </label>
          <button onClick={savePrecheck} disabled={saving} data-testid="save-precheck-btn"
            className="w-full h-12 mt-3 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-60 transition-colors text-white text-sm font-bold">
            {saving ? "Saving…" : "Save pre-fight check"}
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!postOpen} onOpenChange={() => setPostOpen(null)}>
        <DialogContent className="max-w-md" data-testid="postcheck-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">Post-fight check — {postOpen?.name}</DialogTitle>
          </DialogHeader>
          <ObsFields values={postForm} onChange={(k, v) => setPostForm((f) => ({ ...f, [k]: v }))} prefix="post" />
          <textarea rows={2} placeholder="Treatment given (dressings, ice, medication…)" value={postForm.treatment}
            onChange={(e) => setPostForm((f) => ({ ...f, treatment: e.target.value }))}
            data-testid="post-treatment" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mt-2 focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
          <textarea rows={2} placeholder="Post-fight findings (injuries, handed over to hospital, refused care…)" value={postForm.notes}
            onChange={(e) => setPostForm((f) => ({ ...f, notes: e.target.value }))}
            data-testid="post-notes" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
          <label className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-[#F4F7FA] px-3 py-3 mt-2 cursor-pointer">
            <input type="checkbox" checked={postForm.create_patient} onChange={(e) => setPostForm((f) => ({ ...f, create_patient: e.target.checked }))}
              data-testid="post-create-prf" className="h-5 w-5 accent-[#005EB8]" />
            <span className="text-sm font-semibold text-[#0F2547]">Create patient record (PRF) for hospital handover / full documentation</span>
          </label>
          <button onClick={savePostcheck} disabled={saving} data-testid="save-postcheck-btn"
            className="w-full h-12 mt-3 rounded-lg bg-[#007F3B] hover:bg-[#006631] disabled:opacity-60 transition-colors text-white text-sm font-bold">
            {saving ? "Saving…" : "Save post-fight check"}
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editOpen} onOpenChange={() => setEditOpen(null)}>
        <DialogContent className="max-w-sm" data-testid="fighter-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">Edit fighter</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-1">
            <input required placeholder="Fighter name *" value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              data-testid="edit-fighter-name" className={inputCls} />
            <input type="email" placeholder="Email (for the form link)" value={editForm.email}
              onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
              data-testid="edit-fighter-email" className={inputCls} />
            <input type="tel" placeholder="Mobile (for a text)" value={editForm.phone}
              onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
              data-testid="edit-fighter-phone" className={inputCls} />
            <select value={editForm.corner} onChange={(e) => setEditForm((f) => ({ ...f, corner: e.target.value }))}
              data-testid="edit-fighter-corner" className={inputCls}>
              <option value="">No corner assigned</option>
              <option value="red">Red corner</option>
              <option value="blue">Blue corner</option>
            </select>
            <button onClick={saveEdit} disabled={saving || !editForm.name.trim()} data-testid="edit-fighter-save"
              className="w-full h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-60 transition-colors text-white text-sm font-bold">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={chaseOpen} onOpenChange={setChaseOpen}>
        <DialogContent className="max-w-sm" data-testid="chase-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">Email missing forms</DialogTitle>
          </DialogHeader>
          {missing.length === 0 ? (
            <p className="text-sm text-[#007F3B] font-semibold">Every fighter has submitted their form.</p>
          ) : (
            <>
              <p className="text-xs text-slate-500">Not submitted: <span className="font-bold text-[#0F2547]">{missing.map((f) => f.name).join(", ")}</span></p>
              <input type="email" placeholder="Promoter / client email to send the list to" value={chaseEmail}
                onChange={(e) => setChaseEmail(e.target.value)}
                data-testid="chase-email-input" className={inputCls} />
              <button onClick={async () => { const r = await act(() => api.post(`/events/${eid}/chase-missing`, { email: chaseEmail })); if (r) setChaseOpen(false); }}
                data-testid="chase-send-btn"
                className="w-full h-12 rounded-lg bg-[#ED8B00] hover:bg-[#c97600] transition-colors text-white text-sm font-bold">
                Send the list
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
